import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OfficeLayout from '../../components/OfficeLayout';
import { StatusBadge } from '../../components/StatusBadge';
import { getRecord, getDrivers, allocateDriver, resendEmail, getAuditTrail, updateRecord, deleteRecord, getFileUrl, formatApiError } from '../../utils/api';
import { ArrowLeft, Truck, Mail, RefreshCw, Clock, FileText, Download, Trash2 } from 'lucide-react';

export default function RecordDetail() {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [audit, setAudit] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [rRes, dRes] = await Promise.all([getRecord(id), getDrivers()]);
      setRecord(rRes.data);
      setDrivers(dRes.data.filter(d => d.status === 'active'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const loadAudit = async () => {
    try { const { data } = await getAuditTrail(id); setAudit(data); setShowAudit(true); } catch (e) { console.error(e); }
  };

  const handleAllocate = async () => {
    if (!selectedDriver) return;
    setActionLoading(true); setMsg('');
    try {
      await allocateDriver(id, selectedDriver);
      setMsg('Driver allocated successfully!');
      setShowAllocate(false);
      load();
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setActionLoading(false); }
  };

  const handleResendEmail = async () => {
    setActionLoading(true); setMsg('');
    try {
      await resendEmail(id);
      setMsg('Email sent successfully!');
      load();
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this record? This cannot be undone.')) return;
    setActionLoading(true); setMsg('');
    try {
      await deleteRecord(id);
      navigate('/active-records');
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setActionLoading(false); }
  };

  const r = record;
  const isTS = r?.document_type === 'trip_sheet';
  const refNo = isTS ? r?.trip_sheet_fields?.trip_sheet_no : r?.waybill_fields?.waybill_no;
  const editable = r && !['signed', 'completed', 'archived'].includes(r.status);
  const canAllocate = r && ['created'].includes(r.status) && r.source_pdf_url;
  const canResend = r && ['signed', 'email_exception', 'completed'].includes(r.status);

  if (loading) return <OfficeLayout><div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div></div></OfficeLayout>;
  if (!record) return <OfficeLayout><p className="text-center py-16 text-[#6B7280]">Record not found.</p></OfficeLayout>;

  const Field = ({ label, value }) => (
    <div><span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{label}</span><p className="text-sm text-[#1A1A2E] mt-0.5">{value || '—'}</p></div>
  );

  return (
    <OfficeLayout>
      <div data-testid="record-detail-page">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1A1A2E] mb-4"><ArrowLeft size={16} /> Back</button>

        {msg && <div className="mb-4 px-4 py-3 rounded-lg border text-sm bg-blue-50 border-blue-200 text-blue-700">{msg}</div>}

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-2xl font-bold text-[#1A1A2E]">{refNo || 'Record'}</h2>
              <StatusBadge status={r.status} />
            </div>
            <p className="text-sm text-[#6B7280] capitalize">{r.document_type.replace('_', ' ')} &middot; Created by {r.created_by_name || '—'}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canAllocate && <button onClick={() => setShowAllocate(true)} className="flex items-center gap-1.5 px-4 py-2 bg-[#0D9488] text-white rounded-lg text-sm font-medium" data-testid="allocate-button"><Truck size={14} /> Allocate Driver</button>}
            {canResend && <button onClick={handleResendEmail} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="resend-email-button"><Mail size={14} /> Resend Email</button>}
            <button onClick={loadAudit} className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-[#6B7280] hover:bg-gray-50" data-testid="audit-trail-button"><Clock size={14} /> Audit Trail</button>
            {editable && <button onClick={handleDelete} disabled={actionLoading} className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-red-700" data-testid="delete-record-button"><Trash2 size={14} /> Delete</button>}
          </div>
        </div>

        {/* Allocate Modal */}
        {showAllocate && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
            <h3 className="text-sm font-bold mb-3">Allocate Driver</h3>
            <div className="flex gap-3">
              <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="allocate-driver-select">
                <option value="">Select driver...</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <button onClick={handleAllocate} disabled={!selectedDriver || actionLoading} className="px-4 py-2 bg-[#0D9488] text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="confirm-allocate-button">Allocate</button>
              <button onClick={() => setShowAllocate(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        )}

        {/* Record Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Record Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="3rd Party Agent" value={r.third_party_agent} />
              <Field label="Dispatching Hub" value={r.dispatching_hub} />
              <Field label="Email Address" value={r.email_address} />
              <Field label="Driver" value={r.allocated_driver_name} />
              {isTS && <>
                <Field label="Trip Sheet No." value={r.trip_sheet_fields?.trip_sheet_no} />
                <Field label="No. of Waybills" value={r.trip_sheet_fields?.no_of_waybills} />
                <Field label="No. of Items" value={r.trip_sheet_fields?.no_of_items} />
                <Field label="Total Weight" value={r.trip_sheet_fields?.total_weight ? `${r.trip_sheet_fields.total_weight} kg` : '—'} />
              </>}
              {!isTS && <>
                <Field label="Waybill No." value={r.waybill_fields?.waybill_no} />
                <Field label="Description" value={r.waybill_fields?.description} />
                <Field label="Weight" value={r.waybill_fields?.weight ? `${r.waybill_fields.weight} kg` : '—'} />
              </>}
              {r.notes && <div className="col-span-2"><Field label="Notes" value={r.notes} /></div>}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Delivery Proof</h3>
            {r.receiver_name ? (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Receiver Name" value={r.receiver_name} />
                <Field label="Driver Name" value={r.driver_name} />
                <Field label="Signed At" value={r.signed_at ? new Date(r.signed_at).toLocaleString() : '—'} />
                <Field label="Email Status" value={r.email_status} />
                {r.receiver_notes && <div className="col-span-2"><Field label="Receiver Notes" value={r.receiver_notes} /></div>}
                {r.signature_image_url && <div className="col-span-2"><span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Signature</span><img src={getFileUrl(r.signature_image_url)} alt="Signature" className="mt-1 border border-gray-200 rounded-lg max-h-32" /></div>}
              </div>
            ) : (
              <p className="text-sm text-[#6B7280] py-8 text-center">No proof submitted yet.</p>
            )}
            {/* PDF links */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex gap-3 flex-wrap items-center">
                {r.source_pdf_url && <a href={getFileUrl(r.source_pdf_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#0D9488] font-medium"><FileText size={14} /> Source PDF</a>}
                {r.signed_pdf_url && <a href={getFileUrl(r.signed_pdf_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-green-600 font-medium"><Download size={14} /> Signed Proof</a>}
              </div>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <h3 className="text-sm font-bold text-[#1A1A2E] mb-4">Timeline</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Created" value={r.created_at ? new Date(r.created_at).toLocaleString() : '—'} />
            <Field label="Allocated" value={r.allocated_at ? new Date(r.allocated_at).toLocaleString() : '—'} />
            <Field label="Opened" value={r.opened_at ? new Date(r.opened_at).toLocaleString() : '—'} />
            <Field label="Signed" value={r.signed_at ? new Date(r.signed_at).toLocaleString() : '—'} />
            <Field label="Completed" value={r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'} />
          </div>
        </div>

        {/* Audit Trail */}
        {showAudit && (
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-[#1A1A2E]">Audit Trail</h3>
              <button onClick={() => setShowAudit(false)} className="text-sm text-[#6B7280]">Close</button>
            </div>
            {audit.length === 0 ? (
              <p className="text-sm text-[#6B7280] text-center py-4">No audit events found.</p>
            ) : (
              <div className="space-y-3">
                {audit.map((a, i) => (
                  <div key={a.id || i} className="flex gap-3 text-sm border-b border-gray-100 pb-3 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-[#0D9488] mt-1.5 shrink-0"></div>
                    <div>
                      <p className="font-medium text-[#1A1A2E]">{a.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-[#6B7280] text-xs">{a.user_name} &middot; {new Date(a.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </OfficeLayout>
  );
}
